import { Reorder } from "framer-motion";
import {
  Heading1, Type, Image as ImageIcon, Images, Quote, List as ListIcon,
  Minus, MousePointerClick, Video, GripVertical, Copy, Trash2, Plus,
  Eye, EyeOff, Code,
} from "lucide-react";
import MediaPicker from "@/components/MediaPicker";

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: Heading1 },
  { type: "paragraph", label: "Paragraph", icon: Type },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "gallery", label: "Gallery", icon: Images },
  { type: "quote", label: "Quote", icon: Quote },
  { type: "list", label: "List", icon: ListIcon },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "video", label: "Video", icon: Video },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "embed", label: "Embed", icon: Code },
  { type: "hero", label: "Hero", icon: Heading1 },
  { type: "rich_text", label: "Rich Text", icon: Type },
  { type: "image_text", label: "Image + Text", icon: ImageIcon },
  { type: "cta", label: "CTA", icon: MousePointerClick },
  { type: "statistics", label: "Statistics", icon: ListIcon },
  { type: "feature_cards", label: "Feature Cards", icon: Images },
  { type: "testimonials", label: "Testimonials", icon: Quote },
  { type: "spacer", label: "Spacer", icon: Minus },
  { type: "two_column", label: "Two Columns", icon: ListIcon },
  { type: "three_column", label: "Three Columns", icon: ListIcon },
  { type: "featured_article", label: "Featured Article", icon: Heading1 },
  { type: "featured_news", label: "Featured News", icon: Heading1 },
  { type: "featured_event", label: "Featured Event", icon: Heading1 },
  { type: "featured_opportunity", label: "Featured Opportunity", icon: Heading1 },
  { type: "map", label: "Global Network", icon: Images },
  { type: "newsletter", label: "Newsletter CTA", icon: MousePointerClick },
];

const uid = () => Math.random().toString(36).slice(2, 10);

const defaults = {
  heading: { level: "h2", text: "" },
  paragraph: { text: "" },
  image: { url: "", caption: "" },
  gallery: { media: [] },
  quote: { text: "", author: "" },
  list: { style: "bullet", items: "" },
  button: { text: "", url: "" },
  video: { url: "" },
  divider: {},
  embed: { url: "" },
  hero: { title: "", text: "", image: "", buttonText: "", url: "" },
  rich_text: { text: "" }, image_text: { title: "", text: "", image: "" },
  cta: { title: "", text: "", buttonText: "", url: "" },
  statistics: { items: "" }, feature_cards: { items: "" },
  testimonials: { text: "", author: "" }, spacer: { size: 48 },
  two_column: { items: "" }, three_column: { items: "" },
  featured_article: { slug: "" }, featured_news: { slug: "" }, featured_event: { slug: "" }, featured_opportunity: { slug: "" },
  map: { title: "Global DIR Nation", text: "" }, newsletter: { title: "Stay Connected", text: "", buttonText: "Subscribe" },
};

const inp = "w-full rounded-lg bg-dir-elevated border border-dir-gold/20 px-3 py-2 text-sm text-dir-text placeholder:text-dir-faint focus:outline-none focus:border-dir-gold/60";

export default function BlockEditor({ value = [], onChange, mediaFolder = "articles" }) {
  const blocks = Array.isArray(value) ? value : [];

  const add = (type) => onChange([...blocks, { id: uid(), type, ...defaults[type] }]);
  const update = (id, patch) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));
  const duplicate = (b) => {
    const i = blocks.findIndex((x) => x.id === b.id);
    const copy = { ...b, id: uid() };
    onChange([...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)]);
  };

  return (
    <div data-testid="block-editor">
      <label className="mb-2 block text-xs uppercase tracking-wider text-dir-faint">Content Blocks (drag to reorder)</label>

      <Reorder.Group axis="y" values={blocks} onReorder={onChange} className="space-y-3">
        {blocks.map((b) => {
          const meta = BLOCK_TYPES.find((t) => t.type === b.type);
          const Icon = meta?.icon || Type;
          return (
            <Reorder.Item key={b.id} value={b} className="rounded-xl border border-dir-gold/15 bg-dir-card p-4" data-testid={`block-${b.type}`}>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-dir-gold">
                  <GripVertical className="h-4 w-4 cursor-grab text-dir-faint" />
                  <Icon className="h-4 w-4" />{meta?.label || b.type}
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => update(b.id, { hidden: !b.hidden })} className="rounded-md p-1.5 text-dir-faint hover:text-dir-gold" title={b.hidden ? "Show" : "Hide"}>{b.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                  <button type="button" onClick={() => duplicate(b)} className="rounded-md p-1.5 text-dir-faint hover:text-dir-gold" title="Duplicate"><Copy className="h-3.5 w-3.5" /></button>
                  <button type="button" data-testid={`block-delete-${b.id}`} onClick={() => remove(b.id)} className="rounded-md p-1.5 text-red-400 hover:bg-red-500/10" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>

              {b.type === "heading" && (
                <div className="flex gap-2">
                  <select value={b.level} onChange={(e) => update(b.id, { level: e.target.value })} className={`${inp} w-24`}>
                    <option value="h2">H2</option><option value="h3">H3</option>
                  </select>
                  <input className={inp} placeholder="Heading text" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />
                </div>
              )}
              {b.type === "paragraph" && <textarea rows={3} className={inp} placeholder="Write a paragraph..." value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />}
              {b.type === "image" && (
                <MediaPicker label="Block image" folder={mediaFolder} value={b.media || (b.url ? { public_url: b.url, caption: b.caption } : null)} onChange={(media) => update(b.id, { media, url: media?.public_url || media?.url || "", caption: media?.caption || "", alt_text: media?.alt_text || "" })} />
              )}
              {b.type === "gallery" && (
                <div className="space-y-3">{(b.media || []).map((m, i) => <div key={m.id || i} className="flex items-center gap-2"><img src={m.public_url || m.url} alt={m.alt_text || ""} className="h-16 w-20 rounded object-cover" /><span className="flex-1 truncate text-xs text-dir-muted">{m.name || m.filename}</span><button type="button" onClick={() => update(b.id, { media: b.media.filter((_, x) => x !== i) })} className="text-red-400"><Trash2 className="h-4 w-4" /></button></div>)}<MediaPicker label="Add gallery image" folder={mediaFolder} value={null} onChange={(media) => media && update(b.id, { media: [...(b.media || []), media] })} /></div>
              )}
              {b.type === "quote" && (
                <div className="space-y-2">
                  <textarea rows={2} className={inp} placeholder="Quote text" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />
                  <input className={inp} placeholder="Attribution (optional)" value={b.author} onChange={(e) => update(b.id, { author: e.target.value })} />
                </div>
              )}
              {b.type === "list" && (
                <div className="space-y-2">
                  <select value={b.style} onChange={(e) => update(b.id, { style: e.target.value })} className={`${inp} w-32`}>
                    <option value="bullet">Bulleted</option><option value="number">Numbered</option>
                  </select>
                  <textarea rows={3} className={inp} placeholder="One list item per line" value={b.items} onChange={(e) => update(b.id, { items: e.target.value })} />
                </div>
              )}
              {b.type === "button" && (
                <div className="flex gap-2">
                  <input className={inp} placeholder="Button text" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />
                  <input className={inp} placeholder="Link URL" value={b.url} onChange={(e) => update(b.id, { url: e.target.value })} />
                </div>
              )}
              {b.type === "video" && <MediaPicker label="Video" folder="videos" accept="video/mp4,video/webm,image/*" value={b.media || (b.url ? { public_url: b.url, media_type: "video" } : null)} onChange={(media) => update(b.id, { media, url: media?.public_url || media?.url || "" })} />}
              {b.type === "divider" && <div className="h-px bg-dir-gold/20" />}
              {b.type === "embed" && <input className={inp} placeholder="YouTube, Vimeo, map or embed URL" value={b.url} onChange={(e) => update(b.id, { url: e.target.value })} />}
              {["hero", "image_text", "cta"].includes(b.type) && <div className="space-y-2"><input className={inp} placeholder="Section title" value={b.title} onChange={(e) => update(b.id, { title: e.target.value })} /><textarea className={inp} rows={3} placeholder="Section copy" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />{["hero", "image_text"].includes(b.type) && <MediaPicker label="Section image" folder={mediaFolder} value={b.media || (b.image ? { public_url: b.image } : null)} onChange={(media) => update(b.id, { media, image: media?.public_url || media?.url || "" })} />}{["hero", "cta"].includes(b.type) && <div className="flex gap-2"><input className={inp} placeholder="Button text" value={b.buttonText} onChange={(e) => update(b.id, { buttonText: e.target.value })} /><input className={inp} placeholder="Button URL" value={b.url} onChange={(e) => update(b.id, { url: e.target.value })} /></div>}</div>}
              {b.type === "rich_text" && <textarea rows={6} className={inp} placeholder="Rich section copy" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} />}
              {["statistics", "feature_cards", "two_column", "three_column"].includes(b.type) && <div><textarea rows={5} className={inp} placeholder="One item per line: Title | Description" value={b.items} onChange={(e) => update(b.id, { items: e.target.value })} /><p className="mt-1 text-[11px] text-dir-faint">Use one item per line and separate value/title from description with |</p></div>}
              {b.type === "testimonials" && <div className="space-y-2"><textarea rows={3} className={inp} placeholder="Testimonial" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} /><input className={inp} placeholder="Name / attribution" value={b.author} onChange={(e) => update(b.id, { author: e.target.value })} /></div>}
              {b.type === "spacer" && <input type="number" min="8" max="240" className={inp} value={b.size} onChange={(e) => update(b.id, { size: Number(e.target.value) })} />}
              {["featured_article", "featured_news", "featured_event", "featured_opportunity"].includes(b.type) && <input className={inp} placeholder="Published content slug" value={b.slug} onChange={(e) => update(b.id, { slug: e.target.value })} />}
              {["map", "newsletter"].includes(b.type) && <div className="space-y-2"><input className={inp} placeholder="Section title" value={b.title} onChange={(e) => update(b.id, { title: e.target.value })} /><textarea rows={3} className={inp} placeholder="Section copy" value={b.text} onChange={(e) => update(b.id, { text: e.target.value })} /></div>}
            </Reorder.Item>
          );
        })}
      </Reorder.Group>

      {blocks.length === 0 && <p className="rounded-xl border border-dashed border-dir-gold/20 py-6 text-center text-sm text-dir-faint">No blocks yet. Add your first block below.</p>}

      <div className="mt-4">
        <div className="mb-2 flex items-center gap-1 text-xs uppercase tracking-wider text-dir-faint"><Plus className="h-3.5 w-3.5" />Add block</div>
        <div className="flex flex-wrap gap-2">
          {BLOCK_TYPES.map((t) => (
            <button key={t.type} type="button" data-testid={`add-block-${t.type}`} onClick={() => add(t.type)} className="inline-flex items-center gap-1.5 rounded-lg border border-dir-gold/25 px-3 py-1.5 text-xs text-dir-muted hover:border-dir-gold/60 hover:text-dir-gold transition-colors">
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
